package com.islamicmedia.site.util

/**
 * Campaign tagging for links the WEB renders.
 *
 * A second copy of this exists at server/.../util/Utm.kt: the server does not depend on `:shared`
 * (see CLAUDE.md), so a single shared implementation is not reachable from Ktor code.
 *
 * DO NOT tag internal site-to-site links (hub → leaf, footer → /privacy). First-touch wins so a
 * self-referral would not actually overwrite anything, but it fills the breakdown with our own
 * pages pretending to be traffic sources.
 */
object Utm {

    /** Append `utm_*` tags, picking `?` or `&`, and leaving an already-tagged URL untouched. */
    fun tag(
        url: String,
        source: String,
        medium: String,
        campaign: String? = null,
        content: String? = null,
    ): String {
        if (url.isBlank() || url.contains("utm_source=")) return url
        val params = buildList {
            add("utm_source=${encode(source)}")
            add("utm_medium=${encode(medium)}")
            campaign?.takeIf { it.isNotBlank() }?.let { add("utm_campaign=${encode(it)}") }
            content?.takeIf { it.isNotBlank() }?.let { add("utm_content=${encode(it)}") }
        }
        val hash = url.indexOf('#')
        val base = if (hash >= 0) url.substring(0, hash) else url
        val fragment = if (hash >= 0) url.substring(hash) else ""
        val separator = if (base.contains('?')) "&" else "?"
        return "$base$separator${params.joinToString("&")}$fragment"
    }

    /**
     * Google Play listing URL carrying install attribution.
     *
     * A bare `?utm_source=` on a Play link is INVISIBLE to the app — Play only forwards what is
     * inside its own `referrer` parameter, and only through the Install Referrer API. So the utm
     * string is built, percent-encoded whole, and passed as that single parameter. This is the only
     * thing that makes an install traceable back to the page that produced it.
     *
     * @see com.islamicmedia.shared.platform.captureInstallReferrerOnce for the reading half.
     */
    fun playStoreUrl(source: String, medium: String, campaign: String? = null): String {
        val referrer = buildList {
            add("utm_source=$source")
            add("utm_medium=$medium")
            campaign?.takeIf { it.isNotBlank() }?.let { add("utm_campaign=$it") }
        }.joinToString("&")
        return "$PLAY_LISTING&referrer=${encode(referrer)}"
    }

    /**
     * App Store listing URL.
     *
     * Deliberately untagged. Apple ignores `utm_*` entirely; its only campaign mechanism is
     * `?pt=<providerToken>&ct=<campaign>&mt=8`, and `pt` must be a real provider token from App
     * Store Connect. Inventing one would produce a link that looks tagged and attributes nothing,
     * so iOS installs stay unattributed until that token is added here.
     */
    const val APP_STORE_URL = "https://apps.apple.com/app/fitrah-filter/id6759319238"

    private const val PLAY_LISTING =
        "https://play.google.com/store/apps/details?id=com.lgrs.fitrahfilter"

    /** Percent-encode a query value; anything outside the unreserved set is escaped. */
    private fun encode(value: String): String = buildString {
        value.encodeToByteArray().forEach { byte ->
            val b = byte.toInt() and 0xFF
            val c = b.toChar()
            if ((c in 'a'..'z') || (c in 'A'..'Z') || (c in '0'..'9') || c in "-_.~") {
                append(c)
            } else {
                append('%')
                append(b.toString(16).uppercase().padStart(2, '0'))
            }
        }
    }
}
